import React, { useState, useEffect, useRef } from "react";
import { useNode } from "@craftjs/core";
import "../../styles/components/user/UserComponents.css";
import "../../styles/landing.css";

interface TextProps {
  text?: string;
  fontSize?: number;
  color?: string;
  textAlign?: string;
  fontWeight?: string;
  margin?: string;
  padding?: string;
  className?: string;
  style?: React.CSSProperties; // Add style property
  onClick?: () => void; // Add onClick property
}

export const Text = ({
  text = "Edit this text",
  fontSize = 16,
  color = "#000000",
  textAlign = "left",
  fontWeight = "normal",
  margin = "0",
  padding = "0",
  className,
  style, // Add style to props
  onClick, // Add onClick to props
}: TextProps) => {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
    id,
    nodeName,
  } = useNode((node) => ({
    selected: node.events.selected,
    id: node.id,
    nodeName: node.data.custom?.displayName || node.data.displayName,
  }));

  const [editable, setEditable] = useState(false);
  const [editableContent, setEditableContent] = useState(text);
  const textRef = useRef<HTMLDivElement>(null);

  // Debug useEffect to log when component is rendered or selected
  useEffect(() => {
    console.log("Text component rendered/updated:", {
      id,
      selected,
      text,
    });
  }, [id, selected, text]);

  // Update the editable content when text prop changes
  useEffect(() => {
    setEditableContent(text);
  }, [text]);

  // Save changes when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        textRef.current &&
        !textRef.current.contains(e.target as Node) &&
        editable
      ) {
        setEditable(false);
        // Update the node's text prop
        setProp((props: TextProps) => (props.text = editableContent));
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [editable, editableContent, setProp]);

  const handleContentChange = (e: React.FormEvent<HTMLDivElement>) => {
    setEditableContent(e.currentTarget.textContent || "");
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Ensure the element is properly selected in the editor
    if (!selected) {
      console.log("Text component clicked, should be selected now:", id);
    }
  };

  return (
    <div
      ref={(ref) => {
        if (ref) {
          textRef.current = ref;
          connect(drag(ref));
        }
      }}
      onClick={(e) => {
        e.stopPropagation();
        handleClick(e);
        // Execute onClick if provided
        if (onClick) onClick();
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        setEditable(true);
      }}
      style={{
        fontSize: `${fontSize}px`,
        color,
        textAlign: textAlign as any,
        fontWeight,
        margin,
        padding,
        outline: selected ? "2px solid #0f3460" : "none",
        boxShadow: selected ? "0 0 0 1px #0f3460" : "none",
        position: "relative",
        cursor: selected ? "text" : onClick ? "pointer" : "default",
        userSelect: editable ? "text" : "none",
        ...style, // Spread any additional style props
      }}
      className={`text-component ${className || ""} ${
        selected ? "selected-component" : ""
      }`}
      contentEditable={editable}
      suppressContentEditableWarning={true}
      onInput={handleContentChange}
      onBlur={() => {
        setEditable(false);
        setProp((props: TextProps) => (props.text = editableContent));
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          setEditable(false);
          setProp((props: TextProps) => (props.text = editableContent));
        }
      }}
    >
      {selected && !editable && (
        <div
          className="component-selected-indicator"
          style={{
            position: "absolute",
            top: "-16px",
            left: "0",
            background: "#0f3460",
            color: "white",
            fontSize: "10px",
            padding: "2px 4px",
            borderRadius: "3px 3px 0 0",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          {nodeName || "Text"}
        </div>
      )}
      {text}
    </div>
  );
};

export const TextSettings = () => {
  const {
    actions: { setProp },
    fontSize,
    color,
    textAlign,
    fontWeight,
    margin,
    padding,
    text,
  } = useNode((node) => ({
    fontSize: node.data.props.fontSize || 16,
    color: node.data.props.color || "#000000",
    textAlign: node.data.props.textAlign || "left",
    fontWeight: node.data.props.fontWeight || "normal",
    margin: node.data.props.margin || "0",
    padding: node.data.props.padding || "0",
    text: node.data.props.text || "Edit this text",
  }));

  console.log("TextSettings component rendering", { text, fontSize, color });

  return (
    <div className="settings-container">
      <h4>Text Settings</h4>
      <div className="form-group">
        <label>Text Content</label>
        <textarea
          className="form-control"
          value={text || ""}
          onChange={(e) =>
            setProp((props: TextProps) => (props.text = e.target.value))
          }
        />
      </div>
      <div className="form-group">
        <label>Font Size (px)</label>
        <input
          type="number"
          className="form-control"
          value={fontSize || 16}
          onChange={(e) =>
            setProp(
              (props: TextProps) => (props.fontSize = parseInt(e.target.value))
            )
          }
        />
      </div>
      <div className="form-group">
        <label>Color</label>
        <input
          type="color"
          className="form-control color-picker"
          value={color || "#000000"}
          onChange={(e) =>
            setProp((props: TextProps) => (props.color = e.target.value))
          }
        />
      </div>
      <div className="form-group">
        <label>Text Align</label>
        <select
          className="form-control"
          value={textAlign || "left"}
          onChange={(e) =>
            setProp((props: TextProps) => (props.textAlign = e.target.value))
          }
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
        </select>
      </div>
      <div className="form-group">
        <label>Font Weight</label>
        <select
          className="form-control"
          value={fontWeight || "normal"}
          onChange={(e) =>
            setProp((props: TextProps) => (props.fontWeight = e.target.value))
          }
        >
          <option value="normal">Normal</option>
          <option value="bold">Bold</option>
          <option value="lighter">Lighter</option>
        </select>
      </div>
      <div className="form-group">
        <label>Margin</label>
        <input
          className="form-control"
          value={margin || ""}
          onChange={(e) =>
            setProp((props: TextProps) => (props.margin = e.target.value))
          }
          placeholder="e.g. 0px 0px 10px 0px"
        />
      </div>
      <div className="form-group">
        <label>Padding</label>
        <input
          className="form-control"
          value={padding || ""}
          onChange={(e) =>
            setProp((props: TextProps) => (props.padding = e.target.value))
          }
          placeholder="e.g. 10px"
        />
      </div>
    </div>
  );
};

Text.craft = {
  displayName: "Text",
  props: {
    text: "Edit this text",
    fontSize: 16,
    color: "#000000",
    textAlign: "left",
    fontWeight: "normal",
    margin: "0",
    padding: "0",
    style: undefined, // Add style to craft props
    onClick: undefined, // Add onClick to craft props
  },
  related: {
    settings: TextSettings,
  },
};
